<script setup>
import { RouterLink, useLink } from 'vue-router';
import { computed } from 'vue';
import { isTestMode } from '/app/composables/platform';
import { config } from '/app/config';

const props = defineProps({
  // add @ts-ignore if using TypeScript
  ...RouterLink.props,
  inactiveClass: String,
  domain: {
    type: String,
    default: config.app.baseURL,
  },
});

const {
  // the href to use in a link
  href,
  // boolean ref indicating if the link is active
  isActive,
  // boolean ref indicating if the link is exactly active
  isExactActive,
} = useLink(props);

const isExternalLink = computed(
  () => typeof props.to === 'string' && props.to.startsWith('http')
)

const resolvedDomain = computed(() => {
  const domain = props.domain;
  
  // Check if we're in local environment by port or hostname
  const isLocal = location.hostname.includes('local');
  
  if (isLocal) {
    const modifiedHostname = domain
      .replace(/\.com$/, '.local:4001')
      .replace('https://', 'http://');
    
      return modifiedHostname;
  }
  
  return props.domain;
})

const isSameDomain = computed(() => {
  if (isTestMode) {
    return true;
  }

  const domain = new URL(resolvedDomain.value);
  return domain.hostname === location.hostname;
})

const classes = computed(() => ({
  [props.activeClass || 'router-link-active']: isActive.value,
  [props.exactActiveClass || 'router-link-exact-active']: isExactActive.value,
  [props.inactiveClass || '']: !isActive.value,
}))
</script>

<template>
    <RouterLink v-if="isSameDomain" v-bind="$attrs" :to="props.to">
        <slot />
    </RouterLink>
    <a v-else-if="isExternalLink" v-bind="$attrs" :href="props.to" :class="classes">
        <slot />
    </a>
    <a v-else v-bind="$attrs" :href="`${props.domain}${href}`" :class="classes">
        <slot />
    </a>
</template>